import { put, takeLatest, call } from "redux-saga/effects";
import {
	setListCustomer,
	setDetailCustomer,
	GetListCustomerAsyncPayload,
	getListCustomerAsync,
	GetDetailCustomerAsyncPayload,
	getDetailCustomerAsync,
	UpdateCustomerAsyncPayload,
	updateCustomerAsync,
	GetListPersonalAppointmentAsyncPayload,
	setListPersonalAppointment,
	getListPersonalAppointmentAsync,
	GetListPersonalPurchaseAsyncPayload,
	getListPersonalPurchaseAsync,
	setListPersonalPurchase,
	setLoading,
	setLoadingCustomer,
	GetLoyaltyVouchersAsyncPayload,
	setLoyaltyVoucherCodes,
	getLoyaltyVouchersAsync,
	GetLoyaltyTransactionHistoryAsyncPayload,
	getLoyaltyTransactionHistoryAsync,
	setLoyaltyTransactionHistory,
	GetLoyaltyPointsAsyncPayload,
	getLoyaltyPointsAsync,
} from "./customer.slice";
import { message } from "antd";
import api from "service/apiService";
import { t } from "@lingui/macro";
import { translate } from "utils";
import { PayloadAction } from "@reduxjs/toolkit";
import { ApiResponse } from "types";
import {
	Customer,
	LoyaltyPoints,
	PersonalAppointmentQueryParams,
	PersonalPurchaseQueryParams,
} from "../types";
import { queryDefault } from "store/filter/index.slice";

function* getListCustomerSaga(
	action: PayloadAction<GetListCustomerAsyncPayload>,
) {
	const { query, callback, willNotUpdate } = action.payload;
	let { skip } = query;
	const { take } = query;
	skip = skip * take;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Customer[]>>(
				"/dashboard/customers",
				{ ...query, skip },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		if(!willNotUpdate) {
			yield put({ type: setListCustomer.type, payload: response });
		}
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}

function* getDetailCustomerSaga(
	action: PayloadAction<GetDetailCustomerAsyncPayload>,
) {
	const { id } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<Customer>>(
				`/dashboard/customers/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setDetailCustomer.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* updateCustomerSaga(
	action: PayloadAction<UpdateCustomerAsyncPayload>,
) {
	const { id, data, callback } = action.payload;
	yield put({
		type: setLoadingCustomer.type,
		payload: true,
	});
	const { response, error } = yield call(async () => {
		try {
			const response = await api.put(`/dashboard/customers/${id}`, data);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setDetailCustomer.type, payload: response.data });
		message.success(t`Update successful`);
		yield put({
			type: setLoadingCustomer.type,
			payload: false,
		});
	} else {
		message.error(translate(error.message));
	}
	callback && callback();
}

function* getListPersonalAppointmentSaga(
	action: PayloadAction<GetListPersonalAppointmentAsyncPayload>,
) {
	const { id } = action.payload;
	const { query } = action.payload;
	let { skip } = query;
	const { take } = query;
	skip = skip * take;
	yield put({
		type: setLoading.type,
		payload: true,
	});
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<
				ApiResponse<PersonalAppointmentQueryParams[]>
			>(`/dashboard/customers/${id}/bookings`, {
				...query,
				skip,
				id: null,
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({
			type: setListPersonalAppointment.type,
			payload: response,
		});
		yield put({
			type: setLoading.type,
			payload: false,
		});
	} else {
		message.error(translate(error.message));
	}
}

function* getListPersonalPurchaseSaga(
	action: PayloadAction<GetListPersonalPurchaseAsyncPayload>,
) {
	const { id } = action.payload;
	const { query } = action.payload;
	let { skip } = query;
	const { take } = query;
	skip = skip * take;
	yield put({
		type: setLoading.type,
		payload: true,
	});
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<
				ApiResponse<PersonalPurchaseQueryParams[]>
			>(`/dashboard/customers/${id}/purchase-orders`, {
				...query,
				skip,
				id: null,
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setListPersonalPurchase.type, payload: response });
		yield put({
			type: setLoading.type,
			payload: false,
		});
	} else {
		message.error(translate(error.message));
	}
}

function* getLoyaltyVouchersSaga(
	action: PayloadAction<GetLoyaltyVouchersAsyncPayload>,
) {
	const { userId, userType, query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<
				ApiResponse<PersonalPurchaseQueryParams[]>
			>("dashboard/vouchers/code", {
				...(query ?? queryDefault),
				userId,
				userType,
				sortColumn: "redemptionDate",
				sortOrder: "desc"
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setLoyaltyVoucherCodes.type, payload: response });
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}

function* getLoyaltyTransactionHistorySaga(
	action: PayloadAction<GetLoyaltyTransactionHistoryAsyncPayload>,
) {
	const { callback, query } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<
				ApiResponse<PersonalPurchaseQueryParams[]>
			>("/dashboard/transaction-histories", {
				...query
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.();
		yield put({ type: setLoyaltyTransactionHistory.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}


function* getLoyaltyPointsSaga(
	action: PayloadAction<GetLoyaltyPointsAsyncPayload>,
) {
	const { user, userType, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<
				ApiResponse<LoyaltyPoints>
			>("dashboard/campaigns/user/points", {
				user,
				userType
			});
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetListCustomer() {
	yield takeLatest(getListCustomerAsync, getListCustomerSaga);
}

function* watchGetListPersonalAppointment() {
	yield takeLatest(
		getListPersonalAppointmentAsync,
		getListPersonalAppointmentSaga,
	);
}

function* watchGetListPersonalPurchase() {
	yield takeLatest(getListPersonalPurchaseAsync, getListPersonalPurchaseSaga);
}

function* watchGetDetailCustomer() {
	yield takeLatest(getDetailCustomerAsync, getDetailCustomerSaga);
}

function* watchUpdateCustomer() {
	yield takeLatest(updateCustomerAsync, updateCustomerSaga);
}

function* watchGetLoyaltyVouchers() {
	yield takeLatest(getLoyaltyVouchersAsync, getLoyaltyVouchersSaga);
}

function* watchGetLoyaltyTransactionHistory() {
	yield takeLatest(getLoyaltyTransactionHistoryAsync, getLoyaltyTransactionHistorySaga);
}

function* watchGetLoyaltyPoints() {
	yield takeLatest(getLoyaltyPointsAsync, getLoyaltyPointsSaga);
}

const customerSaga = [
	watchGetListCustomer(),
	watchGetDetailCustomer(),
	watchUpdateCustomer(),
	watchGetListPersonalAppointment(),
	watchGetListPersonalPurchase(),
	watchGetLoyaltyVouchers(),
	watchGetLoyaltyTransactionHistory(),
	watchGetLoyaltyPoints()
];
export default customerSaga;
